import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import SeoHead from "@/components/SeoHead";
import Reveal from "@/components/sites/Reveal";
import EstateGallery from "@/components/sites/EstateGallery";
import EstatePropertyCard from "@/components/sites/EstatePropertyCard";
import PreviewBadge from "@/components/sites/PreviewBadge";
import { EstateHeader, EstateFooter } from "@/components/sites/EstateChrome";
import { useBrand, useMarket, useTenantPath, useTenantSlug } from "@/lib/brand";
import { api, mediaUrl } from "@/lib/api";
import { takePreloaded } from "@/lib/preloaded";
import { initialProperty, currentUrl, canonicalPath } from "@/lib/ssrData";
import { fmtPrice, t } from "@/lib/terms";
import { statusBadge, PRICE_QUALIFIER_SHORT } from "@/lib/propertyStatus";
import { partBRows, partCRows } from "@/lib/materialInfo";
import { withPreview } from "@/lib/sitePreview";
import { useVisitInterest } from "@/lib/visitInterest";
import { buildPropertyJsonLd, buildBreadcrumbJsonLd } from "@/lib/seo";
import { loadLeaflet } from "@/lib/leaflet";

/**
 * The property page, in the Estate theme.
 *
 * Same rule as the home page: nothing on it is written here. The design's
 * page carries a plot size, a named negotiator with a photograph, a video
 * tour, an open house and three columns of local information (schools,
 * stations, walking times). Of those, only the video tour is a field on a
 * property record. The rest are not invented; the sections simply do not
 * appear, and the ones that do are the ones the agency has actually filled
 * in.
 *
 * The map is the same postcodes.io centroid the classic page uses, and it is
 * captioned as approximate for the same reason: Foundation publishes the
 * street, never which house on it.
 */

/** A Leaflet map of the postcode centroid, loaded only once it is scrolled to. */
function CentroidMap({ coords, label }) {
  const ref = React.useRef(null);
  useEffect(() => {
    let map;
    let cancelled = false;
    if (!coords || !ref.current) return undefined;
    loadLeaflet().then((L) => {
      if (cancelled || !ref.current) return;
      map = L.map(ref.current, { scrollWheelZoom: false, attributionControl: true })
        .setView([coords.latitude, coords.longitude], 14);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap", maxZoom: 18,
      }).addTo(map);
      L.circle([coords.latitude, coords.longitude], { radius: 260, weight: 1 }).addTo(map)
        .bindPopup(label || "");
    }).catch(() => {});
    return () => { cancelled = true; if (map) map.remove(); };
  }, [coords, label]);
  return <div ref={ref} data-testid="estate-map" />;
}

export default function EstateProperty({ preview = true }) {
  const { slug: urlSlug, id } = useParams();
  const brand = useBrand();
  const market = useMarket();
  const biz = brand.business || {};
  const tenantPath = useTenantPath();
  const navigate = useNavigate();
  const { slug: tenantSlug, known: tenantKnown } = useTenantSlug(urlSlug);
  // What the server already fetched and rendered this page from, if it did.
  //
  // Matched on the slug in the URL, not taken on trust: the payload belongs
  // to the page the document was served for, and a client-side navigation
  // to another house must not start from the previous one's record. It also
  // has to be the value the BROWSER's first render starts from, or the tree
  // React hydrates is not the tree the server sent.
  const seeded = useMemo(() => {
    const p0 = initialProperty();
    if (!p0) return null;
    return p0.website_slug === id || p0.id === id ? p0 : null;
  }, [id]);
  const [prop, setProp] = useState(seeded);
  const [similar, setSimilar] = useState([]);
  const [coords, setCoords] = useState(null);
  const [plan, setPlan] = useState("floorplan");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", consent: false, company: "" });
  const [error, setError] = useState(false);
  const p = (href) => withPreview(href, preview);

  useEffect(() => {
    if (!tenantKnown) return undefined;
    let stale = false;
    takePreloaded(`/crm/public/properties/${id}?tenant=${tenantSlug}`, "__fdnProperty")
      .then(({ data }) => { if (!stale) { setProp(data); setError(false); } })
      .catch(() => { if (!stale) setError(true); });
    api.get(`/crm/public/properties?tenant=${tenantSlug}&limit=12`)
      .then(({ data }) => { if (!stale) setSimilar(data.items || []); })
      .catch(() => {});
    return () => { stale = true; };
  }, [id, tenantSlug, tenantKnown]);

  // Land on a UUID, end up on the readable URL. Same reasoning as the
  // classic page: the address bar is what somebody copies.
  useEffect(() => {
    const wanted = prop?.website_slug;
    if (!wanted || wanted === id) return;
    const here = window.location.pathname;
    if (!here.endsWith(`/${id}`)) return;
    window.history.replaceState(window.history.state, "",
      here.slice(0, -id.length) + wanted + window.location.search + window.location.hash);
  }, [prop, id]);

  useEffect(() => {
    if (!prop?.postcode) return undefined;
    let stale = false;
    fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(prop.postcode.trim())}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!stale && d?.result) setCoords(d.result); })
      .catch(() => {});
    return () => { stale = true; };
  }, [prop?.postcode]);

  const letting = prop?.listing_type === "letting";
  const amount = letting ? prop?.rental_pcm : prop?.asking_price;
  const qualifier = letting ? "" : (PRICE_QUALIFIER_SHORT[prop?.price_qualifier] || "");
  const badge = prop ? statusBadge(prop.status, prop.listing_type, market) : null;
  const outcode = String(prop?.postcode || "").trim().split(/\s+/)[0];
  const images = useMemo(() => {
    if (!prop?.images?.length) return [];
    const primary = prop.images.find((i) => i.id === prop.primary_image_id);
    return primary ? [primary, ...prop.images.filter((i) => i !== primary)] : prop.images;
  }, [prop]);

  const info = prop?.material_info || {};
  const partB = partBRows(info, prop?.property_type);
  const partC = partCRows(info);
  const partA = prop ? [
    amount ? ["Price", `${qualifier ? `${qualifier} ` : ""}${fmtPrice(amount, market)}${letting ? " pcm" : ""}`] : null,
    prop.tenure ? ["Tenure", prop.tenure] : null,
    prop.council_tax_band ? ["Council tax", `Band ${String(prop.council_tax_band).toUpperCase()}`] : null,
    prop.lease_years_remaining ? ["Lease", `${prop.lease_years_remaining} years`] : null,
    prop.ground_rent_annual ? ["Ground rent", `£${Number(prop.ground_rent_annual).toLocaleString()} a year`] : null,
    prop.service_charge_annual ? ["Service charge", `£${Number(prop.service_charge_annual).toLocaleString()} a year`] : null,
    letting && prop.deposit_amount ? ["Deposit", fmtPrice(prop.deposit_amount, market)] : null,
  ].filter(Boolean) : [];

  // The design's four. Plot is not a field on any property record, so the
  // fourth is whatever this listing actually has.
  const keyFacts = prop ? [
    // Plain strings: lib/terms.js has no entry for either, because a bedroom
    // is a bedroom in both markets, and t() returns the KEY when it does not
    // know one. This rendered as "BEDROOMS_CAP" on the live page.
    prop.bedrooms ? { k: "Bedrooms", v: prop.bedrooms } : null,
    prop.bathrooms ? { k: "Bathrooms", v: prop.bathrooms } : null,
    prop.size_sqft ? { k: "Internal area", v: prop.size_sqft.toLocaleString(), unit: "sq ft" } : null,
    prop.reception_rooms ? { k: "Receptions", v: prop.reception_rooms }
      : prop.tenure ? { k: "Tenure", v: prop.tenure } : null,
  ].filter(Boolean) : [];

  const others = similar.filter((s) => s.id !== prop?.id).slice(0, 3);
  const street = prop?.address_line1 || "";
  const heading = [street, prop?.town].filter(Boolean);

  // Same as the area page: this visit, in memory, never stored.
  const { noteArea } = useVisitInterest();
  useEffect(() => { if (prop?.town) noteArea(prop.town); }, [prop?.town, noteArea]);

  // A "Video tour" button that goes nowhere is worse than no button, and
  // the field behind it is free text somebody pastes. The API decides this
  // properly at the public projection (publishable_tour_url); this is the
  // same rule again for the preview, which renders the agent's unpublished
  // record, and for the moment before the fetch lands.
  const tourUrl = (() => {
    const raw = String(prop?.virtual_tour_url || "").trim();
    return /^https?:\/\/[^\s/]+\.[^\s/]/i.test(raw) ? raw : "";
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (form.company) return;                       // honeypot
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email so we can come back to you.");
      return;
    }
    setSending(true);
    try {
      // ApplicantRegistration's own field names, not invented ones. Written
      // from memory first and it would have 422'd on every submission:
      // tenant_slug, first_name/last_name, notes, gdpr_consent.
      const [first, ...rest] = form.name.trim().split(/\s+/);
      await api.post("/crm/public/register-applicant", {
        tenant_slug: tenantSlug,
        first_name: first,
        last_name: rest.join(" "),
        email: form.email,
        phone: form.phone,
        listing_type: letting ? "letting" : "sale",
        min_bedrooms: prop.bedrooms || 0,
        postcodes: outcode ? [outcode] : [],
        property_types: prop.property_type ? [prop.property_type] : [],
        // Which property they asked about, in the note the agent reads.
        notes: [`Enquiry about ${heading.join(", ")}`, form.message].filter(Boolean).join("\n\n"),
        source: "property-enquiry",
        // WHICH house, as a link rather than as a sentence. The note
        // below names it in prose, which a person can read and nothing
        // else can: without this the agency cannot count enquiries per
        // listing, the property screen never shows them, and "who asked
        // about Quay Street" has no answer.
        property_id: prop.id,
        gdpr_consent: form.consent,
      });
      setSent(true);
    } catch (err) {
      // Say WHICH field, when the server says so. A 422 on the email
      // address came back as "that did not send", which sends somebody to
      // the phone rather than to the typo. (Found by submitting the form
      // with a .test address, which is a reserved TLD the validator
      // rejects, and spending a minute deciding whether I had broken the
      // payload.)
      const detail = err?.response?.data?.detail;
      const field = Array.isArray(detail) ? detail[0]?.loc?.slice(-1)[0] : null;
      toast.error(field === "email"
        ? "That email address does not look right. Please check it and try again."
        : "That did not send. Please try again, or call us.");
    } finally {
      setSending(false);
    }
  };

  if (error) {
    return (
      <div className="fs">
        <EstateHeader preview={preview} />
        <main id="main" className="section container">
          <h1 className="display-m">This property is no longer available</h1>
          <p className="lede" style={{ marginTop: 16 }}>
            It may have been sold or withdrawn.{" "}
            <Link className="link" to={p(tenantPath("properties"))}>Browse what is on the market</Link>
          </p>
        </main>
        <EstateFooter preview={preview} />
      </div>
    );
  }

  if (!prop) {
    return (
      <div className="fs">
        <EstateHeader preview={preview} />
        <main id="main" className="section container"><p className="lede">Loading…</p></main>
      </div>
    );
  }

  // Sent with the listing, by the same module that writes the title
  // prerender.py serves Google and the one the agent is shown before they
  // publish. Built here, it disagreed with both about the same house.
  const seoTitle = prop.seo?.title;

  return (
    <div className="fs" data-testid="estate-property">
      <SeoHead
        title={seoTitle}
        description={prop.seo?.description}
        image={images[0] ? mediaUrl(images[0].url) : undefined}
        jsonLd={[
          // currentUrl(), not window.location: this runs during render, and
          // the server has no window. It threw there, React swallowed it
          // into a "switched to client rendering" template, and the whole
          // page silently went back to being client-rendered.
          buildPropertyJsonLd(prop, brand, { url: currentUrl(), resolveImageUrl: mediaUrl }),
          buildBreadcrumbJsonLd([
            { name: "Home", url: tenantPath("") },
            { name: "Properties", url: tenantPath("properties") },
            { name: heading.join(", "), url: canonicalPath() },
          ]),
        ]}
      />
      <a className="skip" href="#main">Skip to content</a>
      <EstateHeader preview={preview} />

      <main id="main">
        <div className="container" style={{ paddingTop: 28 }}>
          <ol className="crumbs">
            <li><Link to={p(tenantPath(""))}>Home</Link></li>
            <li><Link to={p(tenantPath("properties"))}>{letting ? t("to_let", market) : "For sale"}</Link></li>
            {prop.town && (
              <li>
                <Link to={p(tenantPath(`area/${prop.town.toLowerCase().replace(/\s+/g, "-")}`))}>{prop.town}</Link>
              </li>
            )}
            <li>{street}</li>
          </ol>

          <EstateGallery
            images={images}
            alt={heading.join(", ")}
            tools={
              <>
                {images.length > 1 && <span className="btn sm">{images.length} photos</span>}
                {prop.floorplan?.url && (
                  <a className="btn sm" href="#plans">Floor plan</a>
                )}
                {tourUrl && (
                  <a className="btn sm" href={tourUrl} target="_blank" rel="noopener noreferrer">
                    Video tour
                  </a>
                )}
              </>
            }
          />

          <div className="prop">
            <article>
              <div className="eyebrow">
                {[letting ? t("to_let", market) : "For sale", prop.property_type, prop.tenure]
                  .filter(Boolean).join(" · ")}
              </div>
              <h1 className="display-l" style={{ marginTop: 14 }}>
                {street}{prop.town ? <>, <span className="serif-i">{prop.town}</span></> : null}
                {outcode ? ` ${outcode}` : ""}
              </h1>
              {badge && <div className="chips" style={{ marginTop: 14 }}><span className="chip">{badge.label}</span></div>}

              {keyFacts.length > 0 && (
                <div className="keyfacts">
                  {keyFacts.map((f) => (
                    <div key={f.k}>
                      <div className="k">{f.k}</div>
                      <div className="v num">{f.v}{f.unit && <small> {f.unit}</small>}</div>
                    </div>
                  ))}
                </div>
              )}

              {prop.description && (
                <Reveal as="section" className="prose">
                  <h2>The home</h2>
                  <p>{prop.description}</p>
                </Reveal>
              )}

              {prop.key_features?.length > 0 && (
                <Reveal as="section" className="prose" style={{ maxWidth: "none" }}>
                  <h2>At a glance</h2>
                  <ul className="features">
                    {prop.key_features.map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                </Reveal>
              )}

              {(partA.length > 0 || partB.length > 0 || partC.length > 0) && (
                <Reveal as="section" className="prose" style={{ maxWidth: "none" }}
                        data-testid="estate-material-info">
                  <h2>Material information</h2>
                  <p className="small" style={{ marginBottom: 14 }}>
                    Provided in line with National Trading Standards guidance (Parts A, B and C).
                  </p>
                  <div className="matinfo">
                    {[["Part A · Essentials", partA], ["Part B · Property", partB],
                      ["Part C · Risks & rights", partC]].map(([title, rows]) => (
                      rows.length > 0 ? (
                        <div key={title}>
                          <h3>{title}</h3>
                          <dl>
                            {rows.map(([k, v]) => <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>)}
                          </dl>
                        </div>
                      ) : null
                    ))}
                  </div>
                </Reveal>
              )}

              {(prop.floorplan?.url || prop.epc_rating) && (
                <Reveal as="section" className="prose" id="plans" style={{ maxWidth: "none" }}>
                  <h2>Floor plan &amp; EPC</h2>
                  <div className="tabs" role="tablist">
                    {prop.floorplan?.url && (
                      <button type="button" role="tab" aria-selected={plan === "floorplan"}
                              onClick={() => setPlan("floorplan")}>Floor plan</button>
                    )}
                    {prop.epc_rating && (
                      <button type="button" role="tab" aria-selected={plan === "epc"}
                              onClick={() => setPlan("epc")}>EPC</button>
                    )}
                  </div>
                  {plan === "floorplan" && prop.floorplan?.url && (
                    <div className="plan">
                      <img src={mediaUrl(prop.floorplan.url)} alt={`Floor plan, ${heading.join(", ")}`} loading="lazy" />
                    </div>
                  )}
                  {plan === "epc" && prop.epc_rating && (
                    <div className="plan" style={{ padding: 28 }}>
                      <div style={{ textAlign: "center" }}>
                        <div className="display-m">{prop.epc_rating}{prop.epc_score ? ` (${prop.epc_score})` : ""}</div>
                        <div className="small" style={{ marginTop: 8 }}>
                          Current energy rating
                          {prop.epc_potential_rating
                            ? `, potential ${prop.epc_potential_rating}${prop.epc_potential_score ? ` (${prop.epc_potential_score})` : ""}`
                            : ""}
                        </div>
                        {prop.epc_certificate_url && (
                          <a className="link" style={{ marginTop: 16 }} href={prop.epc_certificate_url}
                             target="_blank" rel="noopener noreferrer">View the full certificate</a>
                        )}
                      </div>
                    </div>
                  )}
                </Reveal>
              )}

              {coords && (
                <Reveal as="section" className="prose" style={{ maxWidth: "none" }}>
                  <h2>Where it is</h2>
                  <div className="fsmap"><CentroidMap coords={coords} label={heading.join(", ")} /></div>
                  <p className="small" style={{ marginTop: 12 }}>
                    Approximate location, centred on {prop.postcode}.
                  </p>
                </Reveal>
              )}
            </article>

            <aside className="prop-side">
              {amount ? (
                <div className="price num">
                  {qualifier && <small>{qualifier}</small>}
                  {fmtPrice(amount, market)}{letting ? " pcm" : ""}
                </div>
              ) : <div className="price">Price on application</div>}
              <a className="btn" href="#enquire">
                Book a {t("viewing", market)} <span className="arrow" aria-hidden="true">&rarr;</span>
              </a>
              {biz.phone && (
                <a className="btn ghost" href={`tel:${(biz.phone_intl || biz.phone).replace(/\s/g, "")}`}>
                  {biz.phone}
                </a>
              )}
              <div className="agent">
                <span className="ava" aria-hidden="true">{(biz.name || "?").charAt(0)}</span>
                <div>
                  <strong>Marketed by {biz.name}</strong>
                  {biz.email && <div className="small">{biz.email}</div>}
                </div>
              </div>
            </aside>
          </div>
        </div>

        {others.length > 0 && (
          <section className="section tint">
            <div className="container">
              <Reveal className="section-head">
                <div>
                  <div className="eyebrow">Also on the market</div>
                  <h2 className="display-m" style={{ marginTop: 14 }}>
                    Other homes <span className="serif-i">near here</span>
                  </h2>
                </div>
                <Link className="link" to={p(tenantPath("properties"))}>
                  See all <span className="arrow" aria-hidden="true">&rarr;</span>
                </Link>
              </Reveal>
              <div className="grid cols-3">
                {others.map((o) => (
                  <EstatePropertyCard key={o.id} property={o} market={market}
                                      href={p(`${tenantPath("properties")}/${o.website_slug || o.id}`)} />
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="section" id="enquire">
          <div className="container" style={{ maxWidth: 720 }}>
            <div className="eyebrow">Interested?</div>
            <h2 className="display-m" style={{ marginTop: 14 }}>
              Book a {t("viewing", market)} of <span className="serif-i">{street}</span>
            </h2>
            {sent ? (
              <p className="lede" style={{ marginTop: 20 }} data-testid="estate-enquiry-sent">
                Thank you. We have your details and will come back to you.
              </p>
            ) : (
              <form className="form" style={{ marginTop: 24 }} onSubmit={submit}>
                <div className="row">
                  <div className="fld">
                    <label htmlFor="fs-name">Your name</label>
                    <input id="fs-name" value={form.name} required
                           onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </div>
                  <div className="fld">
                    <label htmlFor="fs-email">Email</label>
                    <input id="fs-email" type="email" value={form.email} required
                           onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </div>
                </div>
                <div className="fld">
                  <label htmlFor="fs-phone">Phone (optional)</label>
                  <input id="fs-phone" value={form.phone}
                         onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div className="fld">
                  <label htmlFor="fs-msg">Anything else? (optional)</label>
                  <textarea id="fs-msg" value={form.message}
                            onChange={(e) => setForm({ ...form, message: e.target.value })} />
                </div>
                <input type="text" value={form.company} tabIndex={-1} autoComplete="off"
                       aria-hidden="true" style={{ position: "absolute", left: "-9999px" }}
                       onChange={(e) => setForm({ ...form, company: e.target.value })} />
                <label className="check">
                  <input type="checkbox" checked={form.consent}
                         onChange={(e) => setForm({ ...form, consent: e.target.checked })} />
                  <span>
                    I am happy for {biz.name} to contact me about this property and to store my
                    details in line with their{" "}
                    <Link className="link" style={{ fontSize: 12 }} to={p(tenantPath("privacy"))}>privacy policy</Link>.
                  </span>
                </label>
                <button className="btn" type="submit" disabled={sending}>
                  {sending ? "Sending…" : "Request a viewing"}
                  <span className="arrow" aria-hidden="true">&rarr;</span>
                </button>
              </form>
            )}
          </div>
        </section>
      </main>

      <EstateFooter preview={preview} />
      {preview && <PreviewBadge />}
    </div>
  );
}
